import { useEffect, useState } from 'react';
import { Layers, X } from 'lucide-react';
import baseapi, { API_BASE, useApiOutage } from '../../baseapi';
import { LiveFrame } from '../junctions/LiveFrame';
import { zoneLabel } from '../../utils/zoneLabel';

interface ZoneCam { id: string; name: string; ip?: string | null; state?: string | null }

/** One zone of GET /api/overview ("zones"). */
interface Zone {
  id: string;
  name: string;
  cap: number | null;
  count: number | null;         // people in the zone now
  entered: number | null;       // counted in at the start camera today
  exited: number | null;        // counted out at the end camera today
  inRate: number | null;        // people a minute, last 15 minutes
  outRate: number | null;
  method: 'entry-exit' | 'in-view';   // in-view: no start / end pair that counts, so the people its cameras see now
  passed?: number | null;       // counted past the zone's start line today
  source: string;
  entry_cameras?: ZoneCam[];
  exit_cameras?: ZoneCam[];
}

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-IN'));

/** Zone names without the camera addresses the server puts in them: "Tollgate (192.168.2.138)" -> "Tollgate". */
const noAddress = (name: unknown) => String(name ?? '').replace(/\s*\([\d.,\s]*\d[\d.,\s]*\)/g, '').trim();

/**
 * The queue zones from the city to the Antharalayam, one card each.
 *
 * A zone has a start camera and an end camera. People in the zone = counted in at the start − counted out
 * at the end (GET /api/overview, the same figures as the Overview page). There is one number per zone, not
 * one per queue line: the cameras count everybody who crosses, whichever line they stand in.
 */
/**
 * The queue lines of the cells layout (one row each), as in the temple lane view. The lines have no cameras of
 * their own: one start / end camera pair counts the whole zone, so the zone figure is shared equally between
 * the lines the zone has (they add up to the zone figure exactly).
 */
const QUEUE_LINES = [
  { id: 'G1', name: 'General Line 1', col: '#2148D6' },
  { id: 'G2', name: 'General Line 2', col: '#1E8A4C' },
  { id: 'G3', name: 'General Line 3', col: '#D63A22' },
  { id: 'G4', name: 'Special / ₹100 Line 4', col: '#7A1FA2' },
  { id: 'G5', name: 'VIP / ₹300–₹500 Line 5', col: '#E9C019' },
];
/**
 * Zones whose lines are counted one by one: the counting line of each lane on the zone's entry cameras
 * (key "camera:line" in /api/summary checkpoints[].lines), in the order of QUEUE_LINES. Inside the temple
 * (Q5) 192.168.2.38 counts lanes Q1-Q3 and 192.168.2.41 lanes Q4-Q5. The people in the zone now are shared
 * between these lanes in proportion to the people each lane counted in. Other zones have the three general
 * lines, shared equally.
 */
const ZONE_LANES: Record<string, string[]> = {
  Q5: ['CAM-2-38:Q1', 'CAM-2-38:Q2', 'CAM-2-38:Q3', 'CAM-2-41:Q4', 'CAM-2-41:Q5'],
};
const linesOf = (z: { id: string }) => ZONE_LANES[z.id]?.length ?? 3;
interface LaneCount { m15: number; total: number }

/** Share `total` in whole people in proportion to `weights` (largest remainders); equally when all are 0. */
const apportion = (total: number, weights: number[]) => {
  const sum = weights.reduce((a, w) => a + w, 0);
  if (sum <= 0) return share(total, weights.length);
  const exact = weights.map(w => (total * w) / sum);
  const parts = exact.map(Math.floor);
  const order = exact.map((x, i) => [x - parts[i], i]).sort((a, b) => b[0] - a[0]);
  for (let k = 0; k < total - parts.reduce((a, p) => a + p, 0); k++) parts[order[k][1]] += 1;
  return parts;
};
/** Share `total` between `n` lines in whole people; the first lines take the remainder. */
const share = (total: number, n: number) => Array.from({ length: n }, (_, i) => Math.floor(total / n) + (i < total % n ? 1 : 0));

/** One colour per zone for the tile layout (route order). */
const TILE_COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#06b6d4', '#0d9488'];

/**
 * variant "cells" (default): one row of coloured cells, as on the Queue Operations page.
 * variant "tiles": one outlined card per zone with a large figure, as on the Q-Lines page.
 */
export function ZoneStrip({ variant = 'cells' }: { variant?: 'cells' | 'tiles' }) {
  const [zones, setZones] = useState<Zone[] | null>(null);
  const [failed, setFailed] = useState(false);
  const outage = useApiOutage();     // true only after 2 minutes without an answer: a restart shows no message
  const [view, setView] = useState<{ zone: Zone; label: string } | null>(null);
  // people counted in on each lane of the zones in ZONE_LANES (null until the first answer)
  const [lanes, setLanes] = useState<Record<string, LaneCount> | null>(null);

  useEffect(() => {
    let alive = true;
    const wanted = new Set(Object.values(ZONE_LANES).flat());
    const load = () => {
      baseapi.get('overview')
        .then(r => { if (alive && Array.isArray(r.data?.zones)) { setZones(r.data.zones.map((z: Zone) => ({ ...z, name: noAddress(z.name) }))); setFailed(false); } })
        .catch(() => { if (alive) setFailed(true); });
      if (variant === 'cells') baseapi.get('summary')
        .then(r => {
          if (!alive || !r.data?.checkpoints) return;
          const out: Record<string, LaneCount> = {};
          for (const cp of Object.values(r.data.checkpoints) as { lines?: Record<string, { people?: { toward?: LaneCount } }> }[]) {
            for (const [k, ln] of Object.entries(cp.lines || {})) {
              if (wanted.has(k)) out[k] = { m15: ln.people?.toward?.m15 ?? 0, total: ln.people?.toward?.total ?? 0 };
            }
          }
          setLanes(out);
        })
        .catch(() => { /* keep the last lane figures */ });
    };
    load();
    const t = window.setInterval(load, 5000);
    return () => { alive = false; window.clearInterval(t); };
  }, [variant]);

  /** Cell colour by how full the zone is: the same green -> amber -> red steps as the exit route cells. */
  const fillColor = (pct: number) => (pct >= 90 ? '#E5533D' : pct >= 70 ? '#F0A63A' : pct >= 50 ? '#E8C56B' : pct >= 25 ? '#8BCB85' : '#25CFA2');
  // cells layout: a column of line names, then one column per zone
  const cols = `190px repeat(${zones?.length ?? 1}, minmax(0, 1fr))`;
  const camIcon = (
    <svg viewBox="0 0 24 16" width="13" height="9" aria-hidden="true">
      <path d="M1 3h13v10H1z" fill="currentColor" />
      <path d="M15 6l7-4v12l-7-4z" fill="currentColor" />
    </svg>
  );

  return (
    <div className="bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl p-3 shadow-xs">
      <div className="text-sm tracking-wide text-neutral-900 dark:text-neutral-100 font-bold mb-2.5 flex flex-wrap items-center justify-between gap-2 border-b border-neutral-100 dark:border-[#222733] pb-2">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-blue-500" />
          <span>Queue zones · people in each zone now</span>
        </div>
        <span className="text-[11px] text-neutral-400 font-normal">
          In zone = counted in at the start camera − counted out at the end camera
        </span>
      </div>

      {failed && !zones && outage && <div className="text-xs text-amber-600 dark:text-amber-400 py-2">Zone counts could not be loaded — retrying.</div>}
      {!zones && !(failed && outage) && <div className="text-xs text-neutral-400 py-2">Loading zones…</div>}

      {zones && variant === 'tiles' && (
        <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 ${({ 3: 'lg:grid-cols-4', 4: 'lg:grid-cols-5', 5: 'lg:grid-cols-6', 7: 'lg:grid-cols-8' } as Record<number, string>)[zones.length] ?? 'lg:grid-cols-7'}`}>
          {zones.map((z, i) => {
            const col = TILE_COLORS[i % TILE_COLORS.length];
            // a zone has a figure when its start and end cameras both count (in − out), or - without such a pair -
            // when its cameras see people now ("in-view", e.g. an end camera that is view-only)
            const balanced = z.method === 'entry-exit' && z.count != null;
            const measured = z.count != null;
            const unbalanced = balanced && (z.exited ?? 0) > (z.entered ?? 0);
            const hasCams = (z.entry_cameras?.length || 0) + (z.exit_cameras?.length || 0) > 0;
            return (
              <div key={z.id} className={`rounded-lg border p-2 bg-white dark:bg-[#181b24] min-w-0 ${measured ? '' : 'border-dashed opacity-70'}`}
                style={{ borderColor: measured ? `${col}66` : undefined }}
                title={balanced ? `${z.name}: in today ${fmt(z.entered)}, out today ${fmt(z.exited)}`
                  : measured ? `${z.name}: people its cameras see now (its start and end cameras do not both count, so there is no in − out figure)`
                  : hasCams ? `${z.name}: its counting camera is not giving a picture at the moment, so there is no figure`
                  : `${z.name}: no start / end camera yet`}>
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-extrabold truncate" style={{ color: measured ? col : undefined }}>
                    {zoneLabel(i)} <span className="font-semibold text-neutral-600 dark:text-neutral-300">· {z.name}</span>
                  </span>
                  {unbalanced && <span className="text-[9px] font-bold text-amber-500 shrink-0" title="More people were counted out than in today, so the number inside is not known">check</span>}
                  {hasCams && (
                    <button type="button" onClick={() => setView({ zone: z, label: zoneLabel(i) })} aria-label={`Cameras of ${z.name}`} title={`Open all cameras of ${z.name}`}
                      className="p-1 rounded text-white shrink-0 cursor-pointer hover:brightness-110 active:scale-95" style={{ background: col }}>
                      <svg viewBox="0 0 24 16" width="13" height="9" aria-hidden="true"><path d="M1 3h13v10H1z" fill="currentColor" /><path d="M15 6l7-4v12l-7-4z" fill="currentColor" /></svg>
                    </button>
                  )}
                </div>
                <div className="text-xl font-extrabold text-neutral-900 dark:text-neutral-100 leading-tight">{measured ? fmt(z.count) : '—'}</div>
                <div className="text-[10px] text-neutral-500 truncate">
                  {balanced ? `in ${fmt(z.entered)} · out ${fmt(z.exited)} today`
                    : measured ? `in camera view now${z.passed != null ? ` · passed ${fmt(z.passed)} today` : ''}`
                    : hasCams ? `no picture from its cameras now${z.passed != null ? ` · passed ${fmt(z.passed)} today` : ''}`
                    : 'no start / end camera yet'}
                </div>
              </div>
            );
          })}
          <div className="rounded-lg border-2 border-blue-500/50 p-2 bg-white dark:bg-[#181b24] min-w-0">
            <div className="text-xs font-extrabold text-blue-600 dark:text-blue-400">All queue zones</div>
            <div className="text-xl font-extrabold text-neutral-900 dark:text-neutral-100 leading-tight">
              {fmt(zones.reduce((a, z) => a + (z.count ?? 0), 0))}
            </div>
            <div className="text-[10px] text-neutral-500 truncate">people in the zones now</div>
          </div>
        </div>
      )}

      {zones && variant === 'cells' && (
        <div className="overflow-x-auto w-full pb-1">
          <div className="min-w-[900px] bg-neutral-50/80 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733] rounded-lg overflow-hidden">
            {/* zone names */}
            <div className="grid divide-x divide-neutral-200/60 dark:divide-[#222733] border-b border-neutral-200/60 dark:border-[#222733] bg-neutral-100/70 dark:bg-[#1c202a]"
              style={{ gridTemplateColumns: cols }}>
              <div className="px-2.5 py-1.5 flex items-end text-[9.5px] font-semibold text-neutral-400 uppercase tracking-wider">Queue zone</div>
              {zones.map((z, i) => (
                <div key={z.id} className="px-2.5 py-1.5 min-w-0" title={z.name}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10.5px] text-blue-600 dark:text-blue-400 font-bold tracking-wider">{zoneLabel(i)}</span>
                    <span className="font-mono text-[9px] text-neutral-400 font-semibold">{z.cap ? `cap ${fmt(z.cap)}` : ''}</span>
                  </div>
                  <span className="block text-[10.5px] text-neutral-800 dark:text-neutral-200 font-semibold leading-tight truncate">{z.name}</span>
                </div>
              ))}
            </div>
            {/* one row per queue line: each zone's figure shared equally between the lines, coloured by how full the zone is */}
            <div className="py-1 space-y-1">
              {QUEUE_LINES.map((ln, li) => (
                <div key={ln.id} className="grid gap-1 px-1 items-stretch" style={{ gridTemplateColumns: cols }}>
                  <div className="flex items-center gap-2 px-1.5 min-w-0 text-[10.5px]">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ln.col }} />
                    <span className="font-bold text-neutral-800 dark:text-neutral-200">{ln.id}</span>
                    <span className="ml-auto truncate text-neutral-500 dark:text-neutral-400">{ln.name}</span>
                  </div>
                  {zones.map((z, i) => {
                    const n = linesOf(z);
                    if (li >= n) {
                      return <div key={z.id} className="h-[28px] flex items-center justify-center text-neutral-300 dark:text-neutral-600 text-xs" title={`${z.name} has no ${ln.name}`}>—</div>;
                    }
                    if (z.count == null) {
                      return (
                        <div key={z.id} title={`${z.name}: no start / end camera yet`}
                          className="h-[28px] rounded border border-dashed border-neutral-200 dark:border-[#2a3040] bg-neutral-100/30 dark:bg-[#1f2430]/20 flex items-center justify-center text-neutral-400 text-xs">
                          —
                        </div>
                      );
                    }
                    const pct = z.cap ? Math.round((z.count / z.cap) * 100) : 0;
                    // lanes counted one by one: shared by the people each lane counted in (last 15 min, else today)
                    const keys = ZONE_LANES[z.id];
                    const counts = keys && lanes ? keys.map(k => lanes[k] ?? { m15: 0, total: 0 }) : null;
                    const byM15 = !!counts && counts.some(c => c.m15 > 0);
                    const part = counts
                      ? apportion(z.count, counts.map(c => (byM15 ? c.m15 : c.total)))[li]
                      : share(z.count, n)[li];
                    const lane = counts ? counts[li] : null;
                    const laneName = keys ? `lane ${keys[li].split(':')[1]} on ${keys[li].split(':')[0]}` : '';
                    const hasCams = (z.entry_cameras?.length || 0) + (z.exit_cameras?.length || 0) > 0;
                    return (
                      <div key={z.id} className="h-[28px] rounded flex items-center pl-2 pr-1 shadow-xs transition hover:brightness-105"
                        style={{ background: fillColor(pct) }}
                        title={lane
                          ? `${zoneLabel(i)} ${z.name} · ${ln.name} (${laneName}): ${fmt(part)} of the ${fmt(z.count)} in the zone, in proportion to the people this lane counted in ${byM15 ? 'in the last 15 minutes' : 'today'} · in today ${fmt(lane.total)} · last 15 min ${fmt(lane.m15)}`
                          : `${zoneLabel(i)} ${z.name} · ${ln.name}: ${fmt(part)} (zone ${fmt(z.count)}${z.method === 'entry-exit' ? '' : ' in camera view now'}, shared equally between its ${n} lines: the lines have no cameras of their own)`}>
                        {lane && <span className="w-[68px] shrink-0 truncate font-mono text-[9.5px] font-semibold text-black/55 tabular-nums">in {fmt(lane.total)}</span>}
                        <span className="flex-1 text-center font-mono text-sm font-bold tabular-nums" style={{ color: pct >= 90 ? '#2A0D0A' : '#07231C' }}>{fmt(part)}</span>
                        {hasCams && (
                          <button type="button" onClick={() => setView({ zone: z, label: zoneLabel(i) })}
                            title={`Open all cameras of ${z.name}`} aria-label={`Open all cameras of ${z.name}`}
                            className="p-1 rounded bg-black/40 hover:bg-black/70 text-white transition cursor-pointer flex items-center justify-center shrink-0 active:scale-95">
                            {camIcon}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {/* the zone total the lines add up to, and in / out today */}
            <div className="grid divide-x divide-neutral-200/60 dark:divide-[#222733] border-t border-neutral-200/60 dark:border-[#222733] bg-neutral-100/70 dark:bg-[#1c202a]"
              style={{ gridTemplateColumns: cols }}>
              <div className="px-2.5 py-1 text-[10px] font-semibold text-neutral-500 dark:text-neutral-400 truncate">Zone total · in / out today</div>
              {zones.map(z => {
                const measured = z.method === 'entry-exit' && z.count != null;
                // without an end camera that counts (in-view zones), "in" is still known: people past the start line today
                const inToday = measured ? z.entered : z.passed ?? null;
                const down = z.method !== 'entry-exit' && (z.exit_cameras?.length || 0) > 0
                  ? `out is not counted: its end camera ${z.exit_cameras!.map(c => c.ip ?? c.id).join(', ')} does not count`
                  : '';
                return (
                  <div key={z.id} className="px-2.5 py-1 font-mono text-[10px] text-neutral-500 dark:text-neutral-400 flex items-center justify-between gap-1 min-w-0 tabular-nums">
                    <span className="truncate" title={measured ? 'Counted in at the start camera today' : 'Counted past the start camera today'}>in <b className="text-neutral-800 dark:text-neutral-200">{fmt(inToday)}</b></span>
                    <span className="text-[11.5px] font-extrabold text-neutral-900 dark:text-neutral-100"
                      title={measured ? `People in the zone now: its ${linesOf(z)} lines together` : `People its cameras see now (not in − out: ${down || 'its end camera does not count'})`}>{fmt(z.count)}</span>
                    <span className="truncate" title={down || undefined}>out <b className="text-neutral-800 dark:text-neutral-200">{measured ? fmt(z.exited) : '—'}</b></span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {view && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setView(null)}>
          <div className="w-[1400px] max-w-full bg-[#0f1218] border border-[#262b37] rounded-xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-3 py-2 border-b border-[#262b37] text-neutral-100">
              <span className="text-[13px] font-bold truncate">{view.label} · {view.zone.name}</span>
              <span className="text-[11px] text-neutral-400">{fmt(view.zone.count)} in zone · in {fmt(view.zone.entered)} · out {fmt(view.zone.exited)}</span>
              <button onClick={() => setView(null)} className="ml-auto p-1 rounded hover:bg-white/10 cursor-pointer" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            {/* every camera of the zone: the ones people come in by, then the ones they leave by */}
            <div className="max-h-[80vh] overflow-y-auto">
              {([['Start · people coming in', view.zone.entry_cameras], ['End · people going out', view.zone.exit_cameras]] as [string, ZoneCam[] | undefined][]).map(([side, cams]) => {
                const list = cams ?? [];
                const total = (view.zone.entry_cameras?.length || 0) + (view.zone.exit_cameras?.length || 0);
                return (
                  <div key={side} className="border-b border-[#262b37] last:border-b-0">
                    <div className="px-3 py-1.5 text-[11px] font-semibold text-neutral-300 bg-[#141821]">
                      {side} <span className="font-normal text-neutral-500">· {list.length} camera{list.length === 1 ? '' : 's'}</span>
                    </div>
                    {!list.length && <div className="px-3 py-6 text-center text-xs text-neutral-500">No camera on this side yet</div>}
                    <div className="grid gap-px bg-[#262b37]" style={{ gridTemplateColumns: `repeat(${Math.min(3, Math.max(1, list.length))}, minmax(0, 1fr))` }}>
                      {list.map(cam => {
                        const live = cam.state === 'online' || cam.state === 'standby';
                        return (
                          <div key={cam.id} className="bg-[#0f1218] min-w-0">
                            <div className="px-2.5 py-1 text-[11px] text-neutral-300 flex items-center gap-1.5 min-w-0">
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${live ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                              <span className="font-semibold truncate" title={cam.name}>{cam.name}</span>
                              <span className="text-neutral-500 font-mono text-[10px] shrink-0">{cam.ip ?? cam.id}</span>
                            </div>
                            <div className="aspect-video bg-black">
                              <LiveFrame src={`${API_BASE}cameras/${encodeURIComponent(cam.id)}/snapshot.jpg`} alt={cam.id}
                                size={total > 2 ? 'tile' : 'full'} maxFps={total > 2 ? 6 : 12} className="w-full h-full object-contain" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
